import { getTranslateData } from "@/utils/translator.js";

/**
 * PanelManager handles all panel-related operations including creation,
 * tab management, and panel interactions
 */
export class PanelManager {
  constructor() {
    this.panels = new Map(); // Store panel elements by step ID
    this.activeTabs = new Map(); // Track active tabs by panel
  }

  /**
   * Create panel element with tabs, simple content, links, or articles
   * @param {number} stepId - Step ID for the panel
   * @param {Object} panelConfig - Panel configuration
   * @param {Function} navigationCallback - Callback for navigation
   * @param {Function} hotspotVisibilityCallback - Callback for hotspot visibility updates
   * @param {Array|null} articles - Array of loaded articles for articleId processing
   * @returns {HTMLElement} Panel DOM element
   */
  createPanel(
    stepId,
    panelConfig,
    navigationCallback,
    hotspotVisibilityCallback,
    articles = null,
  ) {
    console.log(
      "Creating panel for step",
      stepId,
      "with config:",
      panelConfig,
    );

    const panel = document.createElement("div");
    panel.className = stepId === 5 ? "detail-panel" : "info-panel";

    // Add position to Panel (left or right - default right)
    if (panelConfig.position === "left") {
      panel.classList.add("panel-left");
    } else {
      panel.classList.add("panel-right");
    }

    const header = document.createElement("div");
    header.className = "panel-header";

    if (panelConfig.showBackButton) {
      const backButton = document.createElement("button");
      backButton.className = "back-button";
      backButton.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M15 18l-6-6 6-6"/>
        </svg>
        Back
      `;

      // Add back button event listener
      backButton.addEventListener("click", () => {
        this.handleBackButton(panelConfig, stepId, navigationCallback);
      });

      header.appendChild(backButton);
    }

    if (panelConfig.title) {
      const title = document.createElement("h3");
      title.textContent = getTranslateData(panelConfig.title);

      // Apply responsive title visibility logic
      this.applyTitleVisibility(title, panelConfig);

      header.appendChild(title);

      const closeButton = document.createElement("button");
      closeButton.className = "close-panel";
      closeButton.innerHTML = "&times;";
      closeButton.addEventListener("click", () => {
        panel.classList.remove("visible");
      });
      header.appendChild(closeButton);
    }

    panel.appendChild(header);

    // Create content area (simple detail or tabs)
    const content = document.createElement("div");
    content.className = panelConfig.tabs
      ? "panel-content has-tabs"
      : "panel-content";

    if (panelConfig.tabs) {
      this.createTabsContent(
        content,
        panelConfig.tabs,
        stepId,
        hotspotVisibilityCallback,
      );
    } else if (panelConfig.content) {
      this.createSimpleContent(content, panelConfig.content);
    } else if (panelConfig.links) {
      this.createLinksContent(content, panelConfig.links, navigationCallback);
    } else if (panelConfig.articleId) {
      console.log(`Processing articleId: ${panelConfig.articleId} for step ${stepId}`);
      this.createArticleContent(content, panelConfig.articleId, articles);
    } else {
      console.warn(`No content configuration found for panel in step ${stepId}`);
    }

    panel.appendChild(content);

    // Store panel reference
    this.panels.set(stepId, {
      element: panel,
      config: panelConfig,
    });

    return panel;
  }

  /**
   * Create tabs content for panel
   * @param {HTMLElement} contentElement - Content container element
   * @param {Array} tabs - Array of tab configurations
   * @param {number} stepId - Step ID
   * @param {Function} hotspotVisibilityCallback - Callback for hotspot visibility updates
   */
  createTabsContent(contentElement, tabs, stepId, hotspotVisibilityCallback) {
    // Create tab navigation
    const tabNav = document.createElement("div");
    tabNav.className = "panel-tabs";

    // Create tab content container
    const tabContentContainer = document.createElement("div");
    tabContentContainer.className = "tab-contents";

    tabs.forEach((tab, index) => {

      // Create tab button
      const tabButton = document.createElement("button");
      tabButton.className = "tab-button";
      tabButton.textContent = getTranslateData(tab.title);
      tabButton.setAttribute("data-tab", tab.id);

      if (index === 0) {
        tabButton.classList.add("active");
        this.activeTabs.set(stepId, tab.id);
      }

      // Add tab click event listener
      tabButton.addEventListener("click", () => {
        this.handleTabClick(tabButton, stepId, hotspotVisibilityCallback);
      });

      tabNav.appendChild(tabButton);

      // Create tab content
      const tabContent = document.createElement("div");
      tabContent.className = "tab-content";
      tabContent.setAttribute("data-tab", tab.id);

      if (index === 0) {
        tabContent.classList.add("active");
      }

      if (typeof tab.content === "string") {
        const textContainer = document.createElement("div");
        textContainer.className = "panel-text-container";
        const contentP = document.createElement("p");
        contentP.textContent = tab.content;
        textContainer.appendChild(contentP);
        tabContent.appendChild(textContainer);
      } else if (tab.content && tab.content.links) {
        this.createLinksContent(tabContent, tab.content.links);
      }

      tabContentContainer.appendChild(tabContent);
    });

    contentElement.appendChild(tabNav);
    contentElement.appendChild(tabContentContainer);
  }

  /**
   * Create simple content for panel
   * @param {HTMLElement} contentElement - Content container element
   * @param {string} content - Content text
   */
  createSimpleContent(contentElement, content) {
    const textContainer = document.createElement("div");
    textContainer.className = "panel-text-container";
    const contentP = document.createElement("p");
    contentP.textContent = getTranslateData(content);
    textContainer.appendChild(contentP);
    contentElement.appendChild(textContainer);
  }

  /**
   * Create links content for panel
   * @param {HTMLElement} contentElement - Content container element
   * @param {Array} links - Array of link configurations
   * @param {Function} navigationCallback - Callback for navigation
   */
  createLinksContent(contentElement, links, navigationCallback) {
    const linksContainer = document.createElement("div");
    linksContainer.className = "panel-links";

    links.forEach((link) => {
      const linkElement = document.createElement("a");
      linkElement.className = "panel-link";
      linkElement.textContent = getTranslateData(link.title);
      linkElement.href = "#";

      linkElement.addEventListener("click", (e) => {
        e.preventDefault();

        if (link.target && navigationCallback) {
          console.log(`Navigating to step: ${link.target}`);
          navigationCallback(link.target);
        }
      });

      linksContainer.appendChild(linkElement);
    });

    contentElement.appendChild(linksContainer);
  }

  /**
   * Handle tab click event
   * @param {HTMLElement} clickedTab - Clicked tab button
   * @param {number} stepId - Step ID
   * @param {Function} hotspotVisibilityCallback - Callback for hotspot visibility updates
   */
  handleTabClick(clickedTab, stepId, hotspotVisibilityCallback) {
    const tabId = clickedTab.getAttribute("data-tab");
    const panelData = this.panels.get(stepId);

    if (!panelData) return;

    const panel = panelData.element;

    // Update active tab
    const allTabButtons = panel.querySelectorAll(".tab-button");
    const allTabContents = panel.querySelectorAll(".tab-content");

    allTabButtons.forEach((btn) => btn.classList.remove("active"));
    allTabContents.forEach((content) => content.classList.remove("active"));

    clickedTab.classList.add("active");
    const targetContent = panel.querySelector(
      `.tab-content[data-tab="${tabId}"]`,
    );
    if (targetContent) {
      targetContent.classList.add("active");
    }

    // Store active tab
    this.activeTabs.set(stepId, tabId);

    // Update hotspot visibility based on active tab
    if (hotspotVisibilityCallback) {
      hotspotVisibilityCallback(stepId, tabId);
    }
  }

  /**
   * Apply responsive title visibility based on device type and configuration
   * @param {HTMLElement} titleElement - Title DOM element
   * @param {Object} panelConfig - Panel configuration
   */
  applyTitleVisibility(titleElement, panelConfig) {
    // Default values (false as requested)
    const showTitleOnMobile = panelConfig.showTitleOnMobile || false;
    const showTitleOnDesktop = panelConfig.showTitleOnDesktop || false;

    if (panelConfig.showBackButton && !showTitleOnMobile && !showTitleOnDesktop) {
      titleElement.style.display = "none";
      return;
    }

    const visibilityClasses = [];

    if (showTitleOnMobile) {
      visibilityClasses.push("block");
    } else {
      visibilityClasses.push("hidden");
    }

    if (showTitleOnDesktop) {
      visibilityClasses.push("md:block");
    } else {
      visibilityClasses.push("md:hidden");
    }

    titleElement.className += ` ${visibilityClasses.join(" ")}`;
  }

  /**
   * Handle back button click
   * @param {Object} panelConfig - Panel configuration
   * @param {number} stepId - Current step ID
   * @param {Function} navigationCallback - Callback for navigation
   */
  handleBackButton(panelConfig, stepId, navigationCallback) {
    if (panelConfig.backTarget) {
      console.log(
        `Back button navigating to target: ${panelConfig.backTarget}`,
      );
      navigationCallback(panelConfig.backTarget);
      return;
    }

    // Fallback to previous step if available
    const panelData = this.panels.get(stepId);
    if (panelData && panelData.config.transitions?.prev) {
      navigationCallback(panelData.config.transitions.prev);
    }
  }

  /**
   * Get active tab for a step
   * @param {number} stepId - Step ID
   * @returns {string|null} Active tab ID or null
   */
  getActiveTab(stepId) {
    return this.activeTabs.get(stepId) || null;
  }

  /**
   * Set active tab for a step
   * @param {number} stepId - Step ID
   * @param {string} tabId - Tab ID to activate
   * @param {Function} hotspotVisibilityCallback - Callback for hotspot visibility updates
   */
  setActiveTab(stepId, tabId, hotspotVisibilityCallback) {
    const panelData = this.panels.get(stepId);
    if (!panelData) return;

    const panel = panelData.element;
    const targetTab = panel.querySelector(`.tab-button[data-tab="${tabId}"]`);

    if (targetTab) {
      this.handleTabClick(targetTab, stepId, hotspotVisibilityCallback);
    }
  }

  /**
   * Show panel with animation
   * @param {number} stepId - Step ID
   */
  showPanel(stepId) {
    const panelData = this.panels.get(stepId);
    if (panelData) {
      panelData.element.classList.add("visible");
    }
  }

  /**
   * Hide panel with animation
   * @param {number} stepId - Step ID
   */
  hidePanel(stepId) {
    const panelData = this.panels.get(stepId);
    if (panelData) {
      panelData.element.classList.remove("visible");
    }
  }

  /**
   * Get panel statistics
   * @returns {Object} Statistics about panels
   */
  getPanelStats() {
    return {
      totalPanels: this.panels.size,
      activeTabs: Object.fromEntries(this.activeTabs),
      panelStepIds: Array.from(this.panels.keys()),
    };
  }

  /**
   * Create article content for panel
   * @param {HTMLElement} contentElement - Content container element
   * @param {string} articleId - Article ID to load
   * @param {Array} articles - Array of available articles
   */
  createArticleContent(contentElement, articleId, articles) {
    console.log(`Creating article content for articleId: ${articleId}`);

    // Find article by ID
    const article = this.findArticleById(articleId, articles);

    if (!article) {
      console.warn(`❌ Article not found for ID: ${articleId}`);
      this.createFallbackContent(contentElement, articleId);
      return;
    }

    console.log(`Article found:`, article);

    // Create article content container
    const articleContainer = document.createElement("div");
    articleContainer.className = "panel-text-container";

    // Add article title if available
    if (article.title || article.nazev) {
      const title = document.createElement("h2");
      title.textContent = article.title || article.nazev;
      title.className = "text-xl font-semibold mb-4";
      articleContainer.appendChild(title);
    }

    // Add article content
    if (article.text) {
      const content = document.createElement("div");
      content.innerHTML = article.text;
      content.className = "article-content";
      articleContainer.appendChild(content);
    } else if (article.content || article.obsah) {
      const content = document.createElement("div");
      content.innerHTML = article.content || article.obsah;
      content.className = "article-content";
      articleContainer.appendChild(content);
    } else if (article.perex) {
      const perex = document.createElement("div");
      perex.innerHTML = article.perex;
      perex.className = "article-perex";
      articleContainer.appendChild(perex);
    }

    contentElement.appendChild(articleContainer);
  }

  /**
   * Find article by ID in the articles array
   * @param {string} articleId - Article ID to search for
   * @param {Array} articles - Array of articles to search in
   * @returns {Object|null} Found article or null
   */
  findArticleById(articleId, articles) {
    if (!articles || !Array.isArray(articles)) {
      console.warn("Articles array is not available or invalid");
      return null;
    }

    // Try different possible ID formats
    if (typeof articleId !== 'string') {
      console.warn(`ArticleId is not a string:`, articleId);
      return null;
    }

    const possibleIds = [
      articleId,
      articleId.replace('/details/', ''),
      articleId.replace('/', ''),
      articleId.toLowerCase(),
      articleId.replace('/details/', '').toLowerCase()
    ].filter(id => typeof id === 'string' && id.length > 0);

    console.log(`Searching for article with possible IDs:`, possibleIds);

    // Search in articles array
    for (const article of articles) {
      // Check various possible ID fields
      const articleIdentifiers = [
        article.id,
        article.slug,
        article.url,
        article.system_name,
        article.nazev_url,
        article.path
      ].filter(Boolean).filter(id => typeof id === 'string');

      for (const possibleId of possibleIds) {
        for (const identifier of articleIdentifiers) {
          try {
            // Enhanced debugging
            console.log(`Checking: identifier="${identifier}" (type: ${typeof identifier}) vs possibleId="${possibleId}" (type: ${typeof possibleId})`);

            if (identifier && typeof identifier === 'string' && typeof possibleId === 'string') {
              // Safe string comparisons with try-catch
              const exactMatch = identifier === possibleId;
              let identifierIncludes = false;
              let possibleIdIncludes = false;

              try {
                identifierIncludes = identifier.includes(possibleId);
              } catch (e) {
                console.warn(`Error in identifier.includes():`, e, `identifier:`, identifier, `possibleId:`, possibleId);
              }

              try {
                possibleIdIncludes = possibleId.includes(identifier);
              } catch (e) {
                console.warn(`Error in possibleId.includes():`, e, `possibleId:`, possibleId, `identifier:`, identifier);
              }

              if (exactMatch || identifierIncludes || possibleIdIncludes) {
                console.log(`Article match found: ${identifier} matches ${possibleId}`);
                return article;
              }
            }
          } catch (error) {
            console.error(`Error in article matching loop:`, error, {
              identifier,
              possibleId,
              identifierType: typeof identifier,
              possibleIdType: typeof possibleId
            });
          }
        }
      }
    }

    console.warn(`No article found for any of the possible IDs:`, possibleIds);
    return null;
  }

  /**
   * Create fallback content when article is not found
   * @param {HTMLElement} contentElement - Content container element
   * @param {string} articleId - Article ID that was not found
   */
  createFallbackContent(contentElement, articleId) {
    const fallbackContainer = document.createElement("div");
    fallbackContainer.className = "panel-text-container";

    const message = document.createElement("div");
    message.innerHTML = `
      <h3 class="text-lg font-semibold mb-2">Content Loading</h3>
      <p class="text-gray-600">Loading content for: ${articleId}</p>
      <p class="text-sm text-gray-500 mt-2">If this persists, please check the article configuration.</p>
    `;
    message.className = "fallback-content";

    fallbackContainer.appendChild(message);
    contentElement.appendChild(fallbackContainer);
  }

  /**
   * Reset panel manager state
   */
  reset() {
    this.panels.clear();
    this.activeTabs.clear();
  }
}

// Export singleton instance
export const panelManager = new PanelManager();
